import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  ActionButton,
  Button,
  Buttons,
  CancelButton,
  Checkbox,
  DateTime,
  DeleteConfirmation,
  Drawer,
  Form,
  FormMessage,
  HelpTooltip,
  Icon,
} from '~/components';
import { useApi, useConfirmation, useSession, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useDocumentTitle, useFeatures, useForm, useIsMounted } from '~/hooks';
import { colors, weights } from '~/styles';
import MicrosoftTeamsMembers from './MicrosoftTeamsMembers';

const UNKNOWN_ORGANIZATION = 'Unknown organization';
const MARKETPLACE_URL = 'https://marketplace.microsoft.com/en-us/product/WA200012456';

const ErrorMessage = styled(FormMessage.Error)`
  margin-bottom: 1.5rem;
`;

const SectionUnderHeading = styled(Form.Section)`
  padding-top: 1rem;
  border-top: none;
`;

const Relationship = styled.div`
  position: relative;
  border: solid 1px ${colors.grey10};
  border-radius: 0.3125rem;
`;

const RelationshipLabel = styled.div`
  position: absolute;
  top: 0;
  left: 0.625rem;
  margin-left: 0.0625rem;
  padding: 0 0.25rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
  background-color: ${colors.white};
  border-radius: 0.3125rem;
  transform: translateY(-50%);
  pointer-events: none;
`;

const RelationshipInfo = styled.span`
  display: flex;
  align-items: center;
  padding: 0.75rem 0.875rem;
`;

const RelationshipData = styled.span`
  font-weight: ${weights.bold};
  margin-right: auto;
`;

const RelationshipLink = styled(Link)`
  margin-left: 1rem;
  font-size: 0.875rem;

  .icon {
    font-size: 0.75rem;
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ConsentGranted = styled.span`
  color: ${colors.grey55};
  font-size: 0.875rem;
`;

const ExternalIcon = styled(Icon)`
  margin-left: 0.5rem;
  font-size: 0.75rem;
`;

const Checkboxes = styled.div`
  display: flex;
  flex-direction: column;

  > label:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

const Notification = styled.div`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

export default function MicrosoftTeamsSettings({ onClose }) {
  useDocumentTitle('Microsoft 365 Settings');

  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [{ isSubmitting, message, saved, status }, form] = useForm();
  const [integration, setIntegration] = useState();
  const [notifications, setNotifications] = useState();
  const [isReady, setIsReady] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const drawerRef = useRef();
  const confirmation = useConfirmation();
  const [members, setMembers] = useState();
  const [membersError, setMembersError] = useState(false);
  const [removedIds, setRemovedIds] = useState([]);
  const toast = useToast();
  const { isImpersonation } = useSession();

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).integrations().microsoft.teams.get();

    if (!isMounted.current) return;

    setIntegration(data);
    setNotifications(data.teams.notifications);
    setIsReady(true);
  }, [workspace, api, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).integrations().microsoft.teams.getMembers();
      if (!isMounted.current) return;
      setMembers(data.members);
      setMembersError(false);
    } catch {
      if (!isMounted.current) return;
      setMembersError(true);
    }
  }, [workspace.id, api, isMounted]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleGrantConsent = async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).integrations().microsoft.connection.getAdminConsentUri();
      window.location.href = data.adminConsentUri;
    } catch (error) {
      toast.error(error.message || 'Consent could not be started. Please try again.');
    }
  };

  const handleRemovedChange = (ids) => {
    setDirty(true);
    setRemovedIds(ids);
  };

  const handleNotificationChange = (event) => {
    setDirty(true);
    setNotifications((notifications) => ({ ...notifications, [event.target.name]: event.target.checked }));
  };

  const features = useFeatures();

  if (!isReady || !integration || !integration.isConnected) return null;
  return (
    <Drawer
      isOpen
      ref={drawerRef}
      title="Microsoft 365 Settings"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          if (removedIds.length > 0) {
            const count = removedIds.length;
            const confirmed = await confirmation.prompt((resolve) => (
              <DeleteConfirmation resolve={resolve} title="Remove Microsoft Teams Link" deleteLabel="Remove">
                Remove the Microsoft Teams link for {count} {count === 1 ? 'member' : 'members'}? They&apos;ll be asked
                to link their Ruddr account again the next time they message Ruddr in Teams.
              </DeleteConfirmation>
            ));
            if (!confirmed) return;
          }

          form.submit();
          const microsoftTeams = api.www.workspaces(workspace.id).integrations().microsoft.teams;
          try {
            for (const memberId of removedIds) {
              await microsoftTeams.removeLink(memberId);
              setRemovedIds((ids) => ids.filter((id) => id !== memberId));
            }
            await microsoftTeams.updateNotifications(notifications);
            form.save();
            closeDrawer();
          } catch (error) {
            form.error({ message: error.message });
            drawerRef.current.scrollTo({ top: 0 });
          } finally {
            if (removedIds.length > 0) fetchMembers();
          }
        };

        return (
          <>
            {status && <ErrorMessage>{message || 'An error has occurred.'}</ErrorMessage>}
            <Form.Section
              title="Organization"
              subtitle="A Ruddr workspace admin connects a Microsoft 365 organization by selecting Install for Microsoft 365 on this page, or by opening Ruddr in Microsoft Teams and selecting Connect Ruddr to your organization.">
              <Form.Control>
                <Relationship data-testid="ms-teams-tenant">
                  <RelationshipLabel>Organization</RelationshipLabel>
                  <RelationshipInfo>
                    <RelationshipData>{integration.connection.tenantDomain ?? UNKNOWN_ORGANIZATION}</RelationshipData>
                  </RelationshipInfo>
                </Relationship>
              </Form.Control>
              <Form.Control>
                <Relationship>
                  <RelationshipLabel>Connected By</RelationshipLabel>
                  <RelationshipInfo>
                    <RelationshipData>{integration.connection.connectedByMemberName}</RelationshipData>
                  </RelationshipInfo>
                </Relationship>
              </Form.Control>
              <Form.Control>
                <Relationship>
                  <RelationshipLabel>Connected</RelationshipLabel>
                  <RelationshipInfo>
                    <RelationshipData>
                      <DateTime value={integration.connection.createdAt} />
                    </RelationshipData>
                  </RelationshipInfo>
                </Relationship>
              </Form.Control>
              <Form.Control>
                <ActionRow>
                  <Button
                    as="a"
                    className="button"
                    isOutline
                    href={MARKETPLACE_URL}
                    target="_blank"
                    rel="noopener noreferrer">
                    Deploy Ruddr to your organization <ExternalIcon icon="external-link-alt" />
                  </Button>
                  <HelpTooltip message="Optional. A Microsoft 365 admin can pre-install Ruddr in Teams and Copilot for everyone from Microsoft Marketplace. Members can also add Ruddr from the Teams app store themselves." />
                </ActionRow>
              </Form.Control>
              {(integration.connection.adminConsentedAt || !isImpersonation) && (
                <Form.Control>
                  <ActionRow data-testid="ms-admin-consent">
                    {!isImpersonation && (
                      <>
                        <Button isOutline onClick={handleGrantConsent}>
                          {integration.connection.adminConsentedAt
                            ? 'Grant again'
                            : 'Grant consent for your organization'}{' '}
                          <ExternalIcon icon="external-link-alt" />
                        </Button>
                        <HelpTooltip message="Optional. A Microsoft 365 admin can approve Ruddr's permissions once for everyone, so members don't see a consent prompt when they connect their calendar." />
                      </>
                    )}
                    {integration.connection.adminConsentedAt && (
                      <ConsentGranted>
                        Consent granted on <DateTime value={integration.connection.adminConsentedAt} />.
                      </ConsentGranted>
                    )}
                  </ActionRow>
                </Form.Control>
              )}
            </Form.Section>
            <Form.Subheading>Microsoft Teams</Form.Subheading>
            <SectionUnderHeading
              title="Members"
              subtitle="Members who have linked their Ruddr account in Teams. Clear a member to remove their link when you save.">
              <Form.Control>
                {membersError ? (
                  <FormMessage.Error>Members could not be loaded.</FormMessage.Error>
                ) : (
                  members && (
                    <MicrosoftTeamsMembers members={members} removedIds={removedIds} onChange={handleRemovedChange} />
                  )
                )}
              </Form.Control>
            </SectionUnderHeading>
            <Form.Section
              title="Projects"
              subtitle="A project administrator picks its team channel in the project's settings. Add Ruddr to that team in Microsoft Teams first.">
              <Form.Control>
                <Relationship>
                  <RelationshipLabel>Connected Projects</RelationshipLabel>
                  <RelationshipInfo>
                    <RelationshipData>
                      {integration.teams.projects.connected ?? 0} of {integration.teams.projects.total ?? 0}
                    </RelationshipData>
                    <RelationshipLink to={`/app/${workspace.key}/portfolio/projects`}>Manage</RelationshipLink>
                  </RelationshipInfo>
                </Relationship>
              </Form.Control>
            </Form.Section>
            <Form.Section title="Notifications">
              <Form.Control>
                <Checkboxes>
                  <Checkbox
                    name="missingTimeReminders"
                    label="Missing time reminders"
                    checked={notifications.missingTimeReminders}
                    onChange={handleNotificationChange}
                  />
                  <Checkbox
                    name="unsubmittedTimeReminders"
                    label="Unsubmitted time reminders"
                    checked={notifications.unsubmittedTimeReminders}
                    onChange={handleNotificationChange}
                  />
                  {features.timesheets && (
                    <Checkbox
                      name="unsubmittedTimesheetReminders"
                      label="Unsubmitted timesheet reminders"
                      checked={notifications.unsubmittedTimesheetReminders}
                      onChange={handleNotificationChange}
                    />
                  )}
                  <Checkbox
                    name="timeExpenseApprovalReminders"
                    label="Time and expense approval reminders"
                    checked={notifications.timeExpenseApprovalReminders}
                    onChange={handleNotificationChange}
                  />
                  <Checkbox
                    name="timeExpenseRejected"
                    label="Time and expenses have been rejected"
                    checked={notifications.timeExpenseRejected}
                    onChange={handleNotificationChange}
                  />
                  <Notification>
                    <Checkbox
                      name="projectBudgetHoursWarning"
                      label="Project budget hours warning"
                      checked={notifications.projectBudgetHoursWarning}
                      onChange={handleNotificationChange}
                    />
                    <HelpTooltip
                      message="A Teams notification will be sent when the budget used reaches 80% and 100%."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Notification>
                  <Checkbox
                    name="projectMemberAdded"
                    label="Project team member added"
                    checked={notifications.projectMemberAdded}
                    onChange={handleNotificationChange}
                  />
                  <Checkbox
                    name="projectStatusChanged"
                    label="Project status changed"
                    checked={notifications.projectStatusChanged}
                    onChange={handleNotificationChange}
                  />
                  <Checkbox
                    name="projectInvoicePublished"
                    label="Project invoice published"
                    checked={notifications.projectInvoicePublished}
                    onChange={handleNotificationChange}
                  />
                  <Checkbox
                    name="projectTaskStatusChanged"
                    label="Project task status changed"
                    checked={notifications.projectTaskStatusChanged}
                    onChange={handleNotificationChange}
                  />
                  <Notification>
                    <Checkbox
                      name="projectTaskBudgetExceeded"
                      label="Project task budget hours warning"
                      checked={notifications.projectTaskBudgetExceeded}
                      onChange={handleNotificationChange}
                    />
                    <HelpTooltip
                      message="A Teams notification will be sent when the budget used reaches 80% and 100%."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Notification>
                  <Checkbox
                    name="clientApprovalSubmitted"
                    label="Client approval submitted"
                    checked={notifications.clientApprovalSubmitted}
                    onChange={handleNotificationChange}
                  />
                </Checkboxes>
              </Form.Control>
            </Form.Section>
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
